/* eslint-disable max-lines */

import { track as mixpanelTrack } from "jiffy-analytics";
import { track as rudderstackTrack } from "common/utils/rudderstack/tracker";
import { getOrderNumber } from "common/utils/neck-label/meta";
import { megabitsPerSecond } from "common/utils/helpers/upload-rate";

// ============================================================================
// Constants
// ============================================================================

/**
 * Centralized event names as constants for type safety and maintainability
 */
export const NECK_LABEL_EVENTS = {
  TEMPLATE_CHOSEN: "neck_label_template_chosen",
  NEXT_BUTTON_CLICKED: "neck_label_next_button_clicked",
  CANCEL_BUTTON_CLICKED: "neck_label_cancel_button_clicked",
  CANCEL_CONFIRMATION_CLICKED: "neck_label_cancel_confirmation_clicked",
  CANCEL_DISMISSED_CLICKED: "neck_label_cancel_dismissed_clicked",
  BACK_BUTTON_CLICKED: "neck_label_back_button_clicked",
  START_DESIGN: "neck_label_start_design",
  SAVE_BUTTON_CLICKED: "neck_label_save_button_clicked",
  IMAGE_UPLOAD_STARTED: "image_upload_started",
  IMAGE_UPLOAD_FAILED: "image_upload_failed",
  IMAGE_UPLOAD_NETWORK_ERROR: "image_upload_network_error",
  IMAGE_UPLOAD_COMPLETED: "image_upload_completed",
  PRODUCT_ADDED: "product_added"
} as const;

/**
 * Step mapping for better maintainability
 */
export const STEP_NUMBERS = {
  STYLES: 1,
  DETAILS: 2,
  SIZES: 3
} as const;

/**
 * Cancel dismissal CTA types for better maintainability
 */
export const DISMISS_CTA_TYPES = {
  X_BUTTON: "X",
  GO_BACK: "go_back"
} as const;

// ============================================================================
// Types
// ============================================================================

type StepNumber = (typeof STEP_NUMBERS)[keyof typeof STEP_NUMBERS];
type DismissCTAType = (typeof DISMISS_CTA_TYPES)[keyof typeof DISMISS_CTA_TYPES];

interface UserDetails {
  user_id?: string | number;
  user_email?: string;
}

interface BaseEventProperties {
  order_number?: string;
}

interface StepEventProperties extends BaseEventProperties {
  step_number: StepNumber | number;
  metadata?: any;
}

interface TemplateEventProperties extends BaseEventProperties {
  template_id?: number;
  template_name?: string;
  template_type?: "custom" | "system_created";
  page?: string;
  location?: string;
}

interface CancelDismissedEventProperties extends BaseEventProperties {
  step_number: StepNumber | number;
  dismissed_cta: DismissCTAType | string;
  metadata?: any;
}

interface ImageUploadEventProperties extends BaseEventProperties {
  page: string;
  type?: string;
  pdp_type?: string;
  file_name: string;
  file_type: string;
  file_size: number;
  catalog_number?: string;
  dpi?: number;
  project_id?: string | null;
  error_message?: string;
}

type NeckLabelEventProperties =
  | StepEventProperties
  | TemplateEventProperties
  | CancelDismissedEventProperties
  | ImageUploadEventProperties;

// ============================================================================
// Core Tracking Functions
// ============================================================================

/**
 * Gets user details from various sources
 */
function getUserDetails(): UserDetails {
  const jiffyUser = window.jiffy?.user;
  const userIdAttr = document.querySelector("[data-user-id]")?.getAttribute("data-user-id");
  const details: UserDetails = {};

  const userId = jiffyUser?.id || userIdAttr;
  if (userId) details.user_id = userId;

  const userEmail = jiffyUser?.email;
  if (userEmail) details.user_email = userEmail;

  return details;
}

/**
 * Core tracking function - sends events to both Mixpanel and Rudderstack
 */
function sendTrackingEvent(eventName: string, properties: NeckLabelEventProperties = {}): void {
  const orderNumber = getOrderNumber();
  const userDetails = getUserDetails();

  const payload = {
    order_number: orderNumber,
    ...properties,
    ...userDetails
  };

  mixpanelTrack(eventName, payload);
  rudderstackTrack(eventName, payload);
}

// ============================================================================
// Event-Specific Tracking Functions
// ============================================================================

export function trackTemplateChosen(
  template: { id: number; name: string; custom?: boolean },
  page: string = "neck_label",
  location: string = "neck_label"
): void {
  sendTrackingEvent(NECK_LABEL_EVENTS.TEMPLATE_CHOSEN, {
    template_id: template.id,
    template_name: template.name,
    template_type: template.custom ? "custom" : "system_created",
    page,
    location
  });
}

export function trackNextButtonClick(stepNumber: StepNumber | number, metadata?: any): void {
  sendTrackingEvent(NECK_LABEL_EVENTS.NEXT_BUTTON_CLICKED, {
    step_number: stepNumber,
    metadata
  });
}

export function trackBackButtonClick(stepNumber: StepNumber | number, metadata?: any): void {
  sendTrackingEvent(NECK_LABEL_EVENTS.BACK_BUTTON_CLICKED, {
    step_number: stepNumber,
    metadata
  });
}

/**
 * Track clicking the entry point to start designing (from PDP, etc.)
 */
export function trackStartDesign(params?: {
  location?: string;
  platform?: string;
  isApp?: boolean;
}): void {
  const {
    location = "pdp",
    platform = window.MOBILE_APP_PLATFORM || "web",
    isApp = Boolean(window.MOBILE_APP)
  } = params || {};

  sendTrackingEvent(NECK_LABEL_EVENTS.START_DESIGN, {
    is_app: isApp,
    location,
    platform
  } as any);
}

/**
 * Track cancel button click
 */
export function trackCancelButtonClick(stepNumber: StepNumber | number, metadata?: any): void {
  sendTrackingEvent(NECK_LABEL_EVENTS.CANCEL_BUTTON_CLICKED, {
    step_number: stepNumber,
    metadata
  });
}

export function trackCancelConfirmationClick(
  stepNumber: StepNumber | number,
  metadata?: any
): void {
  sendTrackingEvent(NECK_LABEL_EVENTS.CANCEL_CONFIRMATION_CLICKED, {
    step_number: stepNumber,
    metadata
  });
}

export function trackCancelDismissedClick(
  stepNumber: StepNumber | number,
  dismissedCta: DismissCTAType | string,
  metadata?: any
): void {
  sendTrackingEvent(NECK_LABEL_EVENTS.CANCEL_DISMISSED_CLICKED, {
    step_number: stepNumber,
    dismissed_cta: dismissedCta,
    metadata
  });
}

export function trackSaveButtonClick(metadata: any): void {
  sendTrackingEvent(NECK_LABEL_EVENTS.SAVE_BUTTON_CLICKED, metadata);
}

// ============================================================================
// Image Upload Tracking Functions
// ============================================================================

export function trackImageUploadStarted(properties: {
  file: File;
  catalogNumber?: string;
  pdpType?: string;
  uploadUuid?: string;
}): void {
  const { file, catalogNumber, pdpType, uploadUuid } = properties;

  sendTrackingEvent(NECK_LABEL_EVENTS.IMAGE_UPLOAD_STARTED, {
    page: "neck_label",
    type: "logo",
    pdp_type: pdpType,
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    catalog_number: catalogNumber,
    dpi: 300,
    project_id: null,
    ...(uploadUuid && { upload_uuid: uploadUuid })
  });
}

export function trackImageUploadFailed(properties: {
  file: File;
  catalogNumber?: string;
  errorMessage?: string;
  isNetworkError?: boolean;
}): void {
  const { file, catalogNumber, errorMessage, isNetworkError = false } = properties;

  const eventName = isNetworkError
    ? NECK_LABEL_EVENTS.IMAGE_UPLOAD_NETWORK_ERROR
    : NECK_LABEL_EVENTS.IMAGE_UPLOAD_FAILED;

  sendTrackingEvent(eventName, {
    page: "neck_label",
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    catalog_number: catalogNumber,
    ...(errorMessage && { error_message: errorMessage })
  });
}

export function trackImageUploadCompleted(properties: {
  file: File;
  artwork: any;
  catalogNumber?: string;
  dpi?: number;
  preCutTransfers?: any;
  artworksCount?: number;
  response?: any;
  multiArtwork?: boolean;
  pdpType?: string;
  uploadedOn?: string;
  uploadStartTime: number;
  validationErrors?: any;
  businessLine?: string;
  orderType?: string;
  uploadUuid?: string;
}): void {
  const {
    file,
    artwork,
    catalogNumber,
    dpi,
    preCutTransfers,
    artworksCount,
    response,
    multiArtwork,
    pdpType,
    uploadedOn,
    uploadStartTime,
    validationErrors,
    businessLine,
    orderType,
    uploadUuid
  } = properties;

  if (!uploadStartTime) return;

  const uploadSpeedMegabitsPerSecond = megabitsPerSecond(
    file.size,
    uploadStartTime,
    performance.now()
  );

  const artworkId = artwork?.id || response?.id;
  const metadata = artwork?.metadata || {};
  const page = uploadedOn || metadata?.uploaded_on || "neck_label";
  const projectId = artwork?.project_id || response?.project_id;
  const imageUrl = artwork?.url;
  const workerEnhancement = artwork?.data?.worker_enhancement ?? false;

  let validationErrorsMapped = validationErrors || artwork?.errors;
  if (!validationErrorsMapped && response?.error) {
    validationErrorsMapped = [response.error];
  }

  const payload = {
    data: {
      filetype: file.type,
      filesize: file.size,
      filename: file.name,
      artwork_filetype: artwork?.filetype,
      artwork_filesize: artwork?.filesize
    },
    upload_speed_megabits_per_second: uploadSpeedMegabitsPerSecond,
    metadata,
    catalog_number: catalogNumber,
    page,
    pdp_type: pdpType,
    validation_errors: validationErrorsMapped,
    multi_artwork: multiArtwork ?? false,
    project_id: projectId,
    artwork_id: artworkId,
    image_url: imageUrl,
    business_line: businessLine,
    success: response?.success,
    worker_enhancement: workerEnhancement,
    ...(orderType && { order_type: orderType }),
    ...(uploadUuid && { upload_uuid: uploadUuid })
  } as any;

  mixpanelTrack(NECK_LABEL_EVENTS.IMAGE_UPLOAD_COMPLETED, payload);
  rudderstackTrack(NECK_LABEL_EVENTS.IMAGE_UPLOAD_COMPLETED, {
    location: page,
    file,
    artwork,
    dpi: dpi || 300,
    business_line: businessLine,
    catalog_number: catalogNumber,
    pre_cut_transfers: preCutTransfers,
    artworks_count: artworksCount || 1
  });
}

export function trackAddToCart(params: {
  catalogNumber?: string;
  pdpType?: string;
  neckLabelSize: string;
  preCut: boolean;
  addedSizes: Array<{ category: string; size: string; quantity: number }>;
  totalQuantity: number;
}): void {
  const orderNumber = getOrderNumber();
  const userDetails = getUserDetails();

  params.addedSizes.forEach(sizeEntry => {
    mixpanelTrack(NECK_LABEL_EVENTS.PRODUCT_ADDED, {
      order_number: orderNumber,
      catalog_number: params.catalogNumber,
      pdp_type: params.pdpType,
      neck_label_size: params.neckLabelSize,
      pre_cut: params.preCut,
      added_sizes: [sizeEntry],
      total_quantity: params.totalQuantity,
      ...userDetails
    });
  });
}

// ============================================================================
// Metadata Builders
// ============================================================================

/**
 * Namespace for metadata builders to keep them organized
 */
export const MetadataBuilders = {
  /**
   * Build metadata for Styles tab (Step 1)
   */
  forStyles(params: {
    selectedStyleId: number | null;
    selectedStyle: any;
    selectedSize: string;
    designData: any;
  }): any {
    const { selectedStyleId, selectedStyle, selectedSize, designData } = params;
    return {
      selected_style_id: selectedStyleId,
      selected_style_name: selectedStyle?.name,
      selected_template_type: selectedStyle?.custom ? "custom" : "system_created",
      selected_size: selectedSize,
      selected_color: designData.selectedColor,
      company_name: designData.companyName,
      website_tagline: designData.websiteTagline,
      remove_background: designData.removeBackground,
      uploaded_artwork_id: designData.uploadedArtworkId
        ? String(designData.uploadedArtworkId)
        : null,
      uploaded_file_name: designData.uploadedFileName
    };
  },

  /**
   * Build metadata for Details tab (Step 2)
   * Currently just passes through the form data
   */
  forDetails(detailsFormData: any): any {
    return detailsFormData;
  },

  /**
   * Build metadata for Sizes tab (Step 3)
   */
  forSizes(garmentSizes: any, preCut: boolean): any {
    return {
      garment_sizes: garmentSizes,
      pre_cut: preCut
    };
  }
};
